/**
 * @deprecated Use MiniAppRenderContext instead. This component uses react-grid-layout
 * which incorrectly interprets w/h layout values. MiniAppRenderContext uses simple
 * flexbox stacking where only y position matters for ordering.
 */
import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { syncAndRetry, checkoutPublished } from '../../git';
import { useProject } from '../../hooks';
import { loadProjectComponents, setTailwindTheme } from '../../utility';
import SpinnerOverlay from '../SpinnerOverlay/SpinnerOverlay';
import { RGPreviewLayout } from '../RGLayout/RGPreviewLayout';
import { TailwindThemeHooks, useB64UrlForProjectImage } from '../..';
import GlobalStyle from './GlobalStyle';
import { MiniAppRouter } from './Router';
import { useHistory, useParams } from 'react-router-dom';

console.warn('[DEPRECATED] MiniApp is deprecated. Use MiniAppRenderContext instead.');

const Styles = styled.div`
  display: flex;
  flexdirection: column;

  .mi-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .mi-workspace {
    flex: 1;
  }
`;

const MiniAppContent = () => {
  const [components, setComponents] = useState([]);
  const [layout, setLayout] = useState({ lg: [] });
  const [isSyncing, setIsSyncing] = useState(false);
  const { pagePath } = useParams<{ pagePath?: string }>();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const history = useHistory();

  const { projectMetadata } = useProject();
  const { siteBackground, setSiteBackground } = TailwindThemeHooks.useSiteBackground();
  const { siteBackgroundColor, siteBackgroundImage } = TailwindThemeHooks.useSiteBackground();

  const load = async (skipSync = false) => {
    if (!skipSync) {
      setIsSyncing(true);
      await syncAndRetry(projectMetadata?.projectName || '', projectMetadata?.projectURL || '');
      setIsSyncing(false);
    }

    try {
      await checkoutPublished(projectMetadata?.projectName || '');
    } catch (error) {
      console.warn('Unable to checkout published tag. Ignoring', error);
    }

    const componentData = await loadProjectComponents(projectMetadata?.projectName || '', {
      pagePath,
    });

    const layoutItemIdSet = new Set(componentData?.layout?.map((layoutItem: any) => layoutItem?.i));

    const pageComponents = componentData?.components.filter((c: any) => layoutItemIdSet.has(c?.id));

    setComponents(pageComponents);
    setLayout({ lg: componentData.layout });

    const themeFilePath = `${window.miDir}/${projectMetadata.projectName}/src/theme.json`;
    const themeContent = await window.pfs.readFile(themeFilePath, 'utf8');
    const theme = JSON.parse(themeContent as string);

    const tailwindTheme = theme.tailwind;
    const siteBackground = theme.siteBackground;
    (window as any).tailwind.config = tailwindTheme;

    setTailwindTheme(tailwindTheme);
    setSiteBackground(siteBackground);
  };

  const backgroundImageUrl = useB64UrlForProjectImage(
    projectMetadata?.projectName || '',
    siteBackgroundImage
  );
  const siteBackgroundStyles = useMemo(() => {
    // if by some chance BOTH are set (which they never should be), prioritize the image
    if (siteBackgroundImage) {
      return {
        backgroundImage: siteBackgroundImage ? `url(${backgroundImageUrl})` : '',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
      };
    }

    if (siteBackgroundColor) {
      return {
        background: siteBackgroundColor ? siteBackgroundColor : '',
      };
    }

    return {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteBackground, siteBackgroundImage, siteBackgroundColor, backgroundImageUrl]);

  const loadBackground = async () => {
    Object.assign(document.documentElement.style, {
      minHeight: '100%',
    });

    Object.assign(document.body.style, {
      minHeight: '100%',
      margin: '0',
      padding: '0',
      ...siteBackgroundStyles,
    });
  };

  useEffect(() => {
    if (!projectMetadata.projectName && !projectMetadata.projectURL) return;

    load(isRedirecting);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectMetadata.projectName, projectMetadata.projectURL, pagePath]);

  useEffect(() => {
    loadBackground();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteBackgroundStyles]);

  useEffect(() => {
    const handleLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Find the closest anchor tag (in case the click is on a child element)
      const anchor = target.closest('a');

      if (!anchor) {
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href) {
        return;
      }

      // Allow-list: do NOT intercept clicks for:
      // - target=_blank or download links
      // - non-http(s) schemes (mailto:, tel:, sms:, javascript:)
      // - external origins
      // - server-handled paths: /oauth/*, /bff/*, /direct-render-zip/*
      const targetAttr = anchor.getAttribute('target');
      if (targetAttr === '_blank' || anchor.hasAttribute('download')) {
        return;
      }
      if (
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        href.startsWith('sms:') ||
        href.startsWith('javascript:')
      ) {
        return;
      }
      try {
        const url = new URL(href, window.location.href);
        if (url.origin !== window.location.origin) {
          return; // external link
        }
        if (
          url.pathname.startsWith('/oauth/') ||
          url.pathname.startsWith('/bff/') ||
          url.pathname.startsWith('/direct-render-zip/')
        ) {
          return; // let the server handle these
        }
      } catch {
        // If parsing fails, let browser handle it
        return;
      }

      // Internal SPA navigation: prevent default and route client-side
      event.preventDefault();
      setIsRedirecting(true);
      history.push(href);
    };

    window.addEventListener('click', handleLinkClick, true);

    return () => {
      window.removeEventListener('click', handleLinkClick, true);
    };
  }, [history]);

  return (
    <div>
      <Styles>
        <GlobalStyle />
        <SpinnerOverlay isVisible={isSyncing} />
        <div className="mi-container">
          <div className="mi-workspace">
            <RGPreviewLayout components={components} layouts={layout} />
          </div>
        </div>
      </Styles>
    </div>
  );
};

const MiniApp = () => {
  return (
    <MiniAppRouter>
      <MiniAppContent />
    </MiniAppRouter>
  );
};

export { MiniApp };
